/**
 * Voorraad — unieke (individueel traceerbare) items, QR, autosleutel-conditie.
 *
 * Spec: docs/specs/30-voorraad-qr-unieke-items-scanner.md (Deel 1 datamodel,
 *       Deel 2 QR, Deel 6 autosleutel, Deel 7 PIN/PUK, Deel 9 dossier).
 *
 * Architectuur:
 *   - `VoorraadArtikel.traceerbaar=true` splitst uniek (dit bestand) van bulk
 *     (`voorraad.ts` / `VoorraadSaldo` / `boekMutatie()`).
 *   - Elk uniek exemplaar (`VoorraadUniekItem`) heeft een eigen QR + status-
 *     levenscyclus; mutaties zijn append-only via `VoorraadUniekGebeurtenis`.
 *   - Uniek-uitgifte koppelt 1:1 aan `UitrustingItem` (keuze 2).
 *   - PIN/PUK staan versleuteld at-rest; de detail-DTO levert NOOIT de waarde,
 *     alleen `pinMasked`/`pukMasked` (aanwezig-ja/nee). Reveal is een aparte,
 *     geauditeerde call.
 *
 * Conventies (gelijk aan de rest van packages/types):
 *   - Geld over de wire als `number` in DTOs (Decimal in de DB).
 *   - Nullable DB-kolommen → `T | null` in DTOs.
 *   - ID-referenties in Zod: `z.string().min(1)` (NIET `.cuid()` — prod heeft
 *     legacy UUID-format ids). Optioneel-nullable velden: `.optional().nullable()`.
 *   - Enums als `as const`-tuples + type + `_LABELS` + (waar zinvol) `_BADGE`.
 */
import { z } from 'zod'
import { vaklocatieHierarchieRefine } from './voorraad'
import { LABEL_PRESET_KEYS, labelOptiesSchema } from './qr-labels'

// ─── Enums ───────────────────────────────────────────────────────────────────

export const VOORRAAD_UNIEK_STATUS = [
  'OP_VOORRAAD',
  'UITGEGEVEN',
  'IN_DRAGER',
  'IN_REPARATIE',
  'DEFECT',
  'VERMIST',
  'AFGESCHREVEN',
] as const
export type VoorraadUniekStatus = (typeof VOORRAAD_UNIEK_STATUS)[number]

export const VOORRAAD_UNIEK_STATUS_LABELS: Record<VoorraadUniekStatus, string> = {
  OP_VOORRAAD:  'Op voorraad',
  UITGEGEVEN:   'Uitgegeven',
  IN_DRAGER:    'In kist',
  IN_REPARATIE: 'In reparatie',
  DEFECT:       'Defect',
  VERMIST:      'Vermist',
  AFGESCHREVEN: 'Afgeschreven',
}

export type VoorraadUniekBadgeVariant =
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'secondary'

export const VOORRAAD_UNIEK_STATUS_BADGE: Record<VoorraadUniekStatus, VoorraadUniekBadgeVariant> = {
  OP_VOORRAAD:  'success',
  UITGEGEVEN:   'info',
  IN_DRAGER:    'info',
  IN_REPARATIE: 'warning',
  DEFECT:       'danger',
  VERMIST:      'danger',
  AFGESCHREVEN: 'secondary',
}

/**
 * Apparatuur-/bedrijfsmiddel-categorieën voor de Materieel > Apparatuur-tab
 * (spec materieel-bedrijfsmiddelen Deel 1). Eén bron voor API-filter en UI;
 * een nieuw apparaattype komt mee door de VoorraadCategorie toe te voegen en
 * 'm hier op te nemen. AUTOSLEUTEL blijft bewust aan voertuigen hangen.
 */
export const APPARATUUR_CATEGORIEEN = [
  'TELEFOON',
  'SIMKAART',
  'LAPTOP',
  'TABLET',
  'ELEKTRONICA',
  'OVERIG',
] as const
export type ApparatuurCategorie = (typeof APPARATUUR_CATEGORIEEN)[number]

export const VOORRAAD_UNIEK_ACTIE = [
  'AANGEMAAKT',
  'UITGEGEVEN',
  'INGENOMEN',
  'STATUS_GEWIJZIGD',
  'VERPLAATST',
  'GEKOPPELD_VOERTUIG',
  'BESTUURDER_GEWIJZIGD',
  'RESERVE_UITGEGEVEN',
  'SIM_GEKOPPELD',
  'PINPUK_INGEZIEN',
  'AFGESCHREVEN',
  'GEMIGREERD',
  'BIJGEWERKT',
  'IN_DRAGER_GEPLAATST',
  'UIT_DRAGER_GEHAALD',
  // batch-71 gesprek-secties (FO-537 A2) - de toestelcode is vastgelegd; nooit de code zelf.
  'TOESTELCODE_GEREGISTREERD',
] as const
export type VoorraadUniekActie = (typeof VOORRAAD_UNIEK_ACTIE)[number]

export const VOORRAAD_UNIEK_ACTIE_LABELS: Record<VoorraadUniekActie, string> = {
  AANGEMAAKT:           'Aangemaakt',
  UITGEGEVEN:           'Uitgegeven',
  INGENOMEN:            'Ingenomen',
  STATUS_GEWIJZIGD:     'Status gewijzigd',
  VERPLAATST:           'Verplaatst',
  GEKOPPELD_VOERTUIG:   'Gekoppeld aan voertuig',
  BESTUURDER_GEWIJZIGD: 'Bestuurder gewijzigd',
  RESERVE_UITGEGEVEN:   'Reservesleutel uitgegeven',
  SIM_GEKOPPELD:        'Simkaart gekoppeld',
  PINPUK_INGEZIEN:      'PIN/PUK ingezien',
  AFGESCHREVEN:         'Afgeschreven',
  GEMIGREERD:           'Gemigreerd',
  BIJGEWERKT:           'Gegevens bijgewerkt',
  IN_DRAGER_GEPLAATST:  'In kist gelegd',
  UIT_DRAGER_GEHAALD:   'Uit kist gehaald',
  TOESTELCODE_GEREGISTREERD: 'Toestelcode geregistreerd',
}

export const VOORRAAD_CONDITIE_MOMENT = [
  'UITGIFTE',
  'INNAME',
  'BESTUURDERSWISSEL',
  'RESERVE_UITGIFTE',
  'INSPECTIE',
] as const
export type VoertuigConditieMoment = (typeof VOORRAAD_CONDITIE_MOMENT)[number]

export const VOORRAAD_CONDITIE_MOMENT_LABELS: Record<VoertuigConditieMoment, string> = {
  UITGIFTE:          'Uitgifte',
  INNAME:            'Inname',
  BESTUURDERSWISSEL: 'Bestuurderswissel',
  RESERVE_UITGIFTE:  'Reserve-uitgifte',
  INSPECTIE:         'Inspectie',
}

/**
 * Vaste foto-hoeken bij een voertuig-conditie-rapport (keuze 7). Vrije string
 * in de DB (`VoertuigConditieFoto.soort`) maar hier vastgelegd zodat de UI
 * exact deze zes slots toont.
 */
export const VOORRAAD_CONDITIE_FOTO_SOORT = [
  'VOOR',
  'ACHTER',
  'LINKS',
  'RECHTS',
  'INTERIEUR',
  'SCHADE',
] as const
export type VoertuigConditieFotoSoort = (typeof VOORRAAD_CONDITIE_FOTO_SOORT)[number]

export const VOORRAAD_CONDITIE_FOTO_SOORT_LABELS: Record<VoertuigConditieFotoSoort, string> = {
  VOOR:      'Voorzijde',
  ACHTER:    'Achterzijde',
  LINKS:     'Linkerzijde',
  RECHTS:    'Rechterzijde',
  INTERIEUR: 'Interieur',
  SCHADE:    'Schade',
}

// ─── DTOs ───────────────────────────────────────────────────────────────────

/** Eén regel in het unieke-items-overzicht (lijst). Bevat NOOIT PIN/PUK. */
export interface VoorraadUniekItemListRowDTO {
  id: string
  /** Interne code: assetTag ?? serienummer ?? imei ?? artikelnummer-suffix. */
  interneCode: string | null
  artikelNaam: string
  categorie: string
  serienummer: string | null
  imei: string | null
  /** Telefoonnummer (telefoon/simkaart) — voor de identificatie-kolom. */
  telefoonnummer: string | null
  /** ICCID (simkaart) — voor de identificatie-kolom. NOOIT PIN/PUK. */
  iccid: string | null
  merk: string | null
  model: string | null
  /** ISO-datum of null — aanschaf (Materieel/Apparatuur-kolom). */
  aankoopdatum: string | null
  /** ISO-datum of null — garantie tot (Materieel/Apparatuur-kolom). */
  garantieTot: string | null
  status: VoorraadUniekStatus
  /** Houder (medewerker) als UITGEGEVEN, anders null. */
  houderNaam: string | null
  /** Locatie als op voorraad, anders null. */
  locatieNaam: string | null
  /**
   * Kist-inname (2026-07) — bij status IN_DRAGER: de kist waarin het exemplaar
   * ligt (voor de doorklik in lijsten). Optioneel: alleen de lijst-endpoints
   * verrijken dit (batch, bounded per pagina).
   */
  inDrager?: { dragerId: string; nummer: string | null; naam: string } | null
  /** true wanneer keuring binnenkort verloopt of verlopen is. */
  keuringWaarschuwing: boolean
  /** Employee-id van de houder als UITGEGEVEN, anders null (FO-059). */
  houderEmployeeId: string | null
  /** ISO-datum of null — verwachte retourdatum/retourplicht als UITGEGEVEN (FO-059). */
  verwachteRetour: string | null
  qrToken: string
  /** FO-151 — vaklocatie-code ("3-1-3") binnen het magazijn; null = geen/uitgegeven. */
  vaklocatie: string | null
  /** FO-301 — true wanneer het artikel een standaard-inhoud-pakket heeft (= een koffer). */
  isKoffer: boolean
}

/**
 * Review B2 (2026-08-21 QR-traceability): lichte exemplaar-optie voor de
 * blanco-label-koppel-picker (`GET /voorraad/uniek/koppel-zoek`). Uitsluitend
 * deze vijf velden — GEEN `aanschafBedrag`, `pinCodeEnc`/`pukCodeEnc`, `iccid`
 * of `telefoonnummer` zoals op `VoorraadUniekItemListRowDTO`. Dit endpoint is
 * portal-veilig (elke `VOORRAAD.canEdit`-houder, ook zonder backoffice-access)
 * en heeft daarom bewust een eigen smalle select, geen veldstripping op een
 * gedeeld select.
 */
export interface VoorraadUniekKoppelZoekOptieDTO {
  id: string
  artikelNaam: string
  interneCode: string | null
  locatieNaam: string | null
  houderNaam: string | null
}

/** Één gebeurtenis in de historie-tijdlijn van een uniek item (append-only). */
export interface VoorraadUniekGebeurtenisDTO {
  id: string
  actie: VoorraadUniekActie
  vanStatus: VoorraadUniekStatus | null
  naarStatus: VoorraadUniekStatus | null
  vanEmployeeNaam: string | null
  naarEmployeeNaam: string | null
  vanLocatieNaam: string | null
  naarLocatieNaam: string | null
  reden: string | null
  notitie: string | null
  /** Geresolveerde actor-naam (audit-conventie: actorNaam ?? actorId). */
  createdByNaam: string
  createdAt: string
}

/**
 * Detail van een uniek item. PIN/PUK worden NOOIT als waarde geleverd — alleen
 * `pinMasked`/`pukMasked` (true = er staat een (versleutelde) waarde). De
 * werkelijke waarde komt uitsluitend via het geauditeerde reveal-endpoint.
 */
export interface VoorraadUniekItemDetailDTO {
  id: string
  artikelId: string
  artikelNaam: string
  categorie: string
  qrToken: string
  status: VoorraadUniekStatus

  // Toewijzing
  locatieId: string | null
  locatieNaam: string | null
  employeeId: string | null
  houderNaam: string | null
  /**
   * Kist-inname (2026-07) — gevuld bij status IN_DRAGER: in welke kar/koffer
   * het exemplaar ligt + de afgeleide plek van die kist ("bij P. van Dam" /
   * locatienaam / "in bus 12-ABC-3"). Null in elke andere status.
   */
  inDrager: {
    dragerId: string
    nummer: string | null
    naam: string
    ladeNummer: number
    plekLabel: string
  } | null

  // Identificatie (groep-velden — afhankelijk van categorie gevuld)
  serienummer: string | null
  assetTag: string | null
  imei: string | null
  telefoonnummer: string | null
  gekoppeldeSimId: string | null
  provider: string | null
  iccid: string | null
  /** true = PIN-code aanwezig (versleuteld); waarde NIET in deze DTO. */
  pinMasked: boolean
  /** true = PUK-code aanwezig (versleuteld); waarde NIET in deze DTO. */
  pukMasked: boolean
  /** batch-71 gesprek-secties (FO-537 A2) - er staat een versleutelde toestelcode; nooit de code zelf. */
  toestelCodeMasked: boolean
  besturingssysteem: string | null
  merk: string | null
  model: string | null
  aankoopdatum: string | null
  garantieTot: string | null
  aanschafBedrag: number | null
  keuringsdatum: string | null
  volgendeKeuring: string | null
  keuringGeblokkeerd: boolean
  houdbaarheidsdatum: string | null
  vervangdatum: string | null
  voertuigId: string | null
  isReserveSleutel: boolean
  uitrustingItemId: string | null
  /** Verwachte retourdatum (ISO-string). Gezet bij uitgifte, gewist bij innemen/afschrijven. */
  verwachteRetour: string | null

  // FO-151 — vaklocatie binnen het magazijn (alleen zinvol bij MAGAZIJN-locatie;
  // leeggemaakt samen met locatieId bij uitgifte).
  stelling: number | null
  plank: number | null
  vak: number | null
  /** Afgeleide code "3-1-3" (formatVaklocatie); null = geen vaklocatie. */
  vaklocatie: string | null
  /**
   * Laatst bekende vaklocatie-code vóór uitgifte (uit de vaklocatie-audit) —
   * retour-suggestie bij innemen. Null wanneer er nooit een vaklocatie was.
   */
  vaklocatieSuggestie: string | null

  opmerkingen: string | null
  createdAt: string
  updatedAt: string

  /** FO-301 — true wanneer het artikel een standaard-inhoud-pakket heeft (= een koffer). */
  heeftStandaardInhoud: boolean

  gebeurtenissen: VoorraadUniekGebeurtenisDTO[]
}

/** Eén voertuig-conditie-rapport (km-stand + conditie + foto's), keuze 7. */
export interface VoertuigConditieFotoDTO {
  id: string
  soort: string
  fileSize: number
  mimeType: string
  createdAt: string
}

export interface VoertuigConditieRapportDTO {
  id: string
  voertuigId: string
  uniekItemId: string | null
  moment: VoertuigConditieMoment
  kilometerstand: number | null
  conditie: string | null
  employeeId: string | null
  employeeNaam: string | null
  createdByNaam: string
  createdAt: string
  fotos: VoertuigConditieFotoDTO[]
}

/**
 * Aggregatie van alle bedrijfsmiddelen die op naam van één medewerker staan
 * (keuze 3): uitrusting-items (bestelmodule + uniek-uitgifte), unieke items
 * (status UITGEGEVEN) en open bulk-uitgiftes (UITGIFTE minus RETOUR).
 */
export interface BedrijfsmiddelUitrustingDTO {
  id: string
  type: string
  /**
   * Leesbare artikelnaam, afgeleid uit `opmerkingen` (het datamodel heeft geen
   * naam-kolom). Null als er niets bruikbaars in de opmerking staat - toon dan
   * het type als omschrijving.
   */
  omschrijving: string | null
  /** Aantal stuks; altijd >= 1 (uit de opmerking, default 1). */
  aantal: number
  /** Volledige herkomst-set-label (bijv. "Kledingset Service"/"PBM-set"), of null. */
  set: string | null
  maat: string | null
  code: string | null
  /** Opmerking zonder interne import-markers, of null. */
  opmerkingen: string | null
  status: string
  uitgereiktOp: string
  ingenomenOp: string | null
  /**
   * batch-45 onboarding (FO-381) — bezit dat bewust zonder voorraadmutatie is
   * geregistreerd. De Middelen-tab toont hiervoor de niet-onderdrukbare
   * markering "Geregistreerd zonder voorraadmutatie" (reconciliatie magazijn).
   */
  zonderMutatie: boolean
}

export interface BedrijfsmiddelBulkUitgifteDTO {
  artikelId: string
  artikelNaam: string
  categorie: string
  /** Netto openstaand: SUM(UITGIFTE) - SUM(RETOUR), > 0. */
  aantal: number
  eenheid: string
}

/**
 * FO-335 (batch-39) — lichte kosten-verwijzing per middel (de middelen-lijst
 * linkt andersom naar de kostenregel). Bewust ZONDER bedragen: deze tab staat
 * achter VOORRAAD.canView, de bedragen achter `canViewCostPrices`.
 */
export interface BedrijfsmiddelKostenRefDTO {
  /** UNIEK_ITEM | BULK_MUTATIE | UITRUSTING_ITEM — matcht op het rij-id. */
  bronType: string
  /** Uniek-item-id / uitrusting-item-id / artikel-id (bulk). */
  refId: string
  /** Kostenmaand 'yyyy-mm' (Europe/Amsterdam). */
  maand: string
  /** Werkvoorraad "waarde ontbreekt" (regel zonder bedrag). */
  waardeOntbreekt: boolean
}

export interface BedrijfsmiddelenDTO {
  uitrustingItems: BedrijfsmiddelUitrustingDTO[]
  uniekeItems: VoorraadUniekItemListRowDTO[]
  bulkUitgiftes: BedrijfsmiddelBulkUitgifteDTO[]
  /** FO-335 — geboekte uitgifte-kosten van deze medewerker (zonder bedragen). */
  kostenRefs?: BedrijfsmiddelKostenRefDTO[]
}

/**
 * Resultaat van het QR-resolve-endpoint (Deel 2.2). Bevat NOOIT PIN/PUK.
 *
 * BLANCO_LABEL/LABEL_VERVALLEN (spec 2026-08-21 §4.4, taak 9) — een blanco
 * QR-label dat nog niet (of niet meer) aan een exemplaar hangt.
 */
// batch-71 kiosk-magazijn (FO-532 §10): MEDEWERKER = de QR op een medewerkerspas. Een
// koffer of kar blijft UNIEK_ITEM (met `samenvatting.drager`). Cross-app-contract: de
// portal-union op `scanner/r/[token]/page.tsx` beweegt mee.
export type QrResolveKind =
  | 'BULK_ARTIKEL'
  | 'UNIEK_ITEM'
  | 'AUTOSLEUTEL'
  | 'BLANCO_LABEL'
  | 'LABEL_VERVALLEN'
  // batch-69 samenstellen (FO-509 §E9) - de QR van een samenstel-opdracht.
  | 'SAMENSTELLING'
  | 'MEDEWERKER'

export interface QrResolveResultDTO {
  kind: QrResolveKind
  id: string
  /** Korte, niet-gevoelige samenvatting voor het scan-resultaat-scherm. */
  samenvatting: string
  /** Alleen gevuld bij kind=AUTOSLEUTEL. */
  voertuigId?: string | null
}

// ─── Zod schemas ────────────────────────────────────────────────────────────
//
// ID-referenties: `z.string().min(1)` (prod heeft legacy UUID-format ids, dus
// NIET `.cuid()`). Optioneel-nullable velden krijgen `.optional().nullable()`
// om de null-rejection-bug te vermijden (zie projectgeheugen diagnose-spurious-
// validatie). Actor (createdById) komt server-side uit de sessie, niet uit de
// body — dus niet in de schemas.

/** Datum-string in ISO of `yyyy-mm-dd`; de route parset naar Date. */
const dateString = z.string().min(1).max(40)

// Basis-object apart gehouden: `.superRefine` levert een ZodEffects op waar
// `.partial()` niet meer op bestaat — create en update refinen elk zelf.
const uniekItemVeldenSchema = z.object({
  artikelId: z.string().min(1),
  locatieId: z.string().min(1).optional().nullable(),
  serienummer: z.string().max(200).optional().nullable(),
  assetTag: z.string().max(200).optional().nullable(),
  imei: z.string().max(50).optional().nullable(),
  telefoonnummer: z.string().max(50).optional().nullable(),
  gekoppeldeSimId: z.string().min(1).optional().nullable(),
  provider: z.string().max(100).optional().nullable(),
  iccid: z.string().max(50).optional().nullable(),
  // PIN/PUK plaintext-invoer; de service versleutelt at-rest (fase 6).
  pinCode: z.string().max(50).optional().nullable(),
  pukCode: z.string().max(50).optional().nullable(),
  besturingssysteem: z.string().max(100).optional().nullable(),
  merk: z.string().max(100).optional().nullable(),
  model: z.string().max(100).optional().nullable(),
  aankoopdatum: dateString.optional().nullable(),
  garantieTot: dateString.optional().nullable(),
  aanschafBedrag: z.number().nonnegative().optional().nullable(),
  keuringsdatum: dateString.optional().nullable(),
  volgendeKeuring: dateString.optional().nullable(),
  keuringGeblokkeerd: z.boolean().optional(),
  houdbaarheidsdatum: dateString.optional().nullable(),
  vervangdatum: dateString.optional().nullable(),
  voertuigId: z.string().min(1).optional().nullable(),
  isReserveSleutel: z.boolean().optional(),
  opmerkingen: z.string().max(2000).optional().nullable(),
  // FO-151 — vaklocatie binnen het magazijn. De payload-hiërarchie wordt via
  // vaklocatieHierarchieRefine gecheckt; de service valideert daarnaast de
  // SAMENGEVOEGDE staat (huidige + gewijzigde velden) én het MAGAZIJN-type.
  stelling: z.number().int().min(1).max(999).optional().nullable(),
  plank: z.number().int().min(0).max(999).optional().nullable(),
  vak: z.number().int().min(0).max(999).optional().nullable(),
})

export const createUniekItemSchema = uniekItemVeldenSchema.superRefine(vaklocatieHierarchieRefine)
export type CreateUniekItemInput = z.infer<typeof createUniekItemSchema>

export const updateUniekItemSchema = uniekItemVeldenSchema.partial().superRefine(vaklocatieHierarchieRefine)
export type UpdateUniekItemInput = z.infer<typeof updateUniekItemSchema>

export const uitgevenSchema = z.object({
  employeeId: z.string().min(1),
  reden: z.string().min(1).max(500),
  datum: dateString.optional().nullable(),
  verwachteRetour: dateString.optional().nullable(),
  /**
   * FO-508 (spec 208) — telefoon en simkaart als set: het nummer (abonnement uit
   * voorraad, of het huidige nummer van de ontvanger bij een vervangend toestel)
   * dat in dezelfde handeling aan deze telefoon wordt gekoppeld.
   */
  abonnementId: z.string().min(1).nullable().optional(),
})
export type UitgevenInput = z.infer<typeof uitgevenSchema>

export const innemenSchema = z
  .object({
    // Bestemming: precies één van locatieId (terug op voorraad) of dragerId
    // (kist-inname: in een kar/koffer leggen). ladeId alleen samen met dragerId.
    locatieId: z.string().min(1).optional().nullable(),
    dragerId: z.string().min(1).optional().nullable(),
    ladeId: z.string().min(1).optional().nullable(),
    conditie: z.string().max(2000).optional().nullable(),
    opmerking: z.string().max(2000).optional().nullable(),
    // FO-151 — vaklocatie direct bij retour zetten (bv. de suggestie uit de
    // audit-historie overnemen). Optioneel; alleen zinvol bij MAGAZIJN.
    stelling: z.number().int().min(1).max(999).optional().nullable(),
    plank: z.number().int().min(0).max(999).optional().nullable(),
    vak: z.number().int().min(0).max(999).optional().nullable(),
    // batch-67 middelen-inname (FO-499 §A3 / FO-505 §12.2) — de staat bij inname.
    // BESCHADIGD zet het exemplaar op DEFECT in plaats van terug op voorraad; bij
    // een staat anders dan GOED hoort minstens één bewijsfoto (vooraf geupload,
    // client-UUID via `POST /middelen/fotos`). Zelfde vier waarden als
    // `OFFBOARDING_INLEVER_STATEN`.
    staat: z.enum(['GOED', 'GEBRUIKSSPOREN', 'BESCHADIGD', 'ONVOLLEDIG']).optional().nullable(),
    fotoIds: z.array(z.string().uuid('Ongeldige foto-verwijzing')).max(10, "Maximaal 10 foto's.").optional(),
  })
  .superRefine(vaklocatieHierarchieRefine)
  .superRefine((val, ctx) => {
    if (val.staat && val.staat !== 'GOED' && (val.fotoIds?.length ?? 0) < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fotoIds'],
        message: 'Maak minimaal een foto bij een staat anders dan goed.',
      })
    }
    const heeftLocatie = val.locatieId != null
    const heeftDrager = val.dragerId != null
    if (heeftLocatie === heeftDrager) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: heeftLocatie ? ['dragerId'] : ['locatieId'],
        message: 'Kies een locatie óf een kar/koffer als bestemming',
      })
    }
    if (val.ladeId != null && !heeftDrager) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ladeId'],
        message: 'Een lade hoort bij een kar/koffer-bestemming',
      })
    }
    if (val.stelling != null && !heeftLocatie) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['stelling'],
        message: 'Een vaklocatie hoort bij een locatie-bestemming',
      })
    }
  })
export type InnemenInput = z.infer<typeof innemenSchema>

export const verplaatsSchema = z.object({
  locatieId: z.string().min(1),
  reden: z.string().max(500).optional().nullable(),
})
export type VerplaatsInput = z.infer<typeof verplaatsSchema>

export const statusWijzigSchema = z.object({
  status: z.enum(VOORRAAD_UNIEK_STATUS),
  reden: z.string().max(500).optional().nullable(),
  notitie: z.string().max(2000).optional().nullable(),
})
export type StatusWijzigInput = z.infer<typeof statusWijzigSchema>

export const koppelSimSchema = z.object({
  simId: z.string().min(1),
})
export type KoppelSimInput = z.infer<typeof koppelSimSchema>

/** PIN/PUK reveal vereist altijd een reden (geauditeerd, Deel 7). */
export const revealSchema = z.object({
  reden: z.string().min(1).max(500),
})
export type RevealInput = z.infer<typeof revealSchema>

/**
 * Reserve-sleutel uitgeven (keuze 8): reden + medewerker + voertuig +
 * verwachte retourdatum ALTIJD verplicht; alleen `opmerking` optioneel.
 */
export const reserveSleutelSchema = z.object({
  employeeId: z.string().min(1),
  voertuigId: z.string().min(1),
  reden: z.string().min(1).max(500),
  verwachteRetour: dateString,
  opmerking: z.string().max(2000).optional().nullable(),
})
export type ReserveSleutelInput = z.infer<typeof reserveSleutelSchema>

/** Bestuurder wisselen op een voertuig (via VoertuigToewijzing-flow). */
export const bestuurderWisselSchema = z.object({
  nieuweEmployeeId: z.string().min(1),
  datum: dateString.optional().nullable(),
  reden: z.string().max(500).optional().nullable(),
})
export type BestuurderWisselInput = z.infer<typeof bestuurderWisselSchema>

/** Voertuig-conditie-rapport (km-stand + conditie + foto-metadata), keuze 7. */
export const conditieRapportSchema = z.object({
  voertuigId: z.string().min(1),
  uniekItemId: z.string().min(1).optional().nullable(),
  moment: z.enum(VOORRAAD_CONDITIE_MOMENT),
  kilometerstand: z.number().int().nonnegative().optional().nullable(),
  conditie: z.string().max(2000).optional().nullable(),
  employeeId: z.string().min(1).optional().nullable(),
})
export type ConditieRapportInput = z.infer<typeof conditieRapportSchema>

/**
 * QR-PDF-export (Deel 2.3): scope + formaat + scope-specifieke parameters.
 *
 * Scope 'filter' (spec portal-werktijden-qr Deel 3): genereer voor ALLE
 * artikelen + bijbehorende unieke items die aan de actieve lijst-filters
 * voldoen (categorie/locatie/traceerbaar/zoek/sinds-datum, allemaal optioneel
 * en combineerbaar) — zodat "selecteer alle gefilterde" geen duizenden ids
 * door de payload hoeft te duwen.
 */
export const qrPdfSchema = z
  .object({
    // FO-151 — scope 'stelling': alle saldo-artikelen + unieke items op één
    // locatie in één stelling (het stelling-etikettenvel, FO-151 §10).
    scope: z.enum([
      'selectie',
      'groep',
      'locatie',
      'nieuw-sinds',
      'filter',
      'stelling',
      'blanco-labels',
    ]),
    formaat: z.enum(['etiket', 'lijst']).default('etiket'),
    /** Etiket-geometrie; alleen van toepassing bij formaat 'etiket'. */
    preset: z.enum(LABEL_PRESET_KEYS).default('a4-24'),
    /**
     * Wat er op elk etiket staat (tekstregels + STREEP-logo); alleen van
     * toepassing bij formaat 'etiket'. Weggelaten/null = het gedrag van vóór
     * deze optie: alle tekstregels aan, geen logo (DEFAULT_LABEL_OPTIES).
     */
    labelOpties: labelOptiesSchema.optional().nullable(),
    ids: z.array(z.string().min(1)).optional().nullable(),
    categorie: z.string().min(1).optional().nullable(),
    locatieId: z.string().min(1).optional().nullable(),
    sindsDatum: dateString.optional().nullable(),
    // Alleen voor scope 'filter' (spiegelt de artikellijst-filters).
    traceerbaar: z.boolean().optional().nullable(),
    search: z.string().trim().min(1).max(100).optional().nullable(),
    // Alleen voor scope 'stelling' (samen met locatieId).
    stelling: z.number().int().min(1).max(999).optional().nullable(),
    // Alleen voor scope 'blanco-labels'.
    serie: z.string().trim().min(1).max(40).optional().nullable(),
    labelIds: z.array(z.string().min(1)).optional().nullable(),
    alleenOngebruikt: z.boolean().optional().nullable(),
  })
  .superRefine((val, ctx) => {
    if (val.scope === 'selectie' && (!val.ids || val.ids.length === 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Selectie vereist minimaal één id', path: ['ids'] })
    }
    if (val.scope === 'groep' && !val.categorie) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Groep vereist een categorie', path: ['categorie'] })
    }
    if (val.scope === 'locatie' && !val.locatieId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Locatie vereist een locatieId', path: ['locatieId'] })
    }
    if (val.scope === 'nieuw-sinds' && !val.sindsDatum) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Nieuw-sinds vereist een sindsDatum', path: ['sindsDatum'] })
    }
    if (val.scope === 'stelling') {
      if (!val.locatieId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Stelling-scope vereist een locatieId', path: ['locatieId'] })
      }
      if (val.stelling == null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Stelling-scope vereist een stellingnummer', path: ['stelling'] })
      }
    }
    if (val.scope === 'blanco-labels' && !val.serie && (!val.labelIds || val.labelIds.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Blanco labels vereist een serie of een selectie',
        path: ['serie'],
      })
    }
    // scope 'filter': geen verplichte velden — zonder filters betekent het
    // bewust "alle artikelen".
  })
export type QrPdfInput = z.infer<typeof qrPdfSchema>

/** Querystring van het QR-resolve-endpoint (Deel 2.2). */
export const qrResolveQuerySchema = z.object({
  token: z.string().min(1).max(64),
})
export type QrResolveQuery = z.infer<typeof qrResolveQuerySchema>

/**
 * Sleutel-uitrol (spec 2026-08-21 §7): hoofd- + reservesleutel per voertuig in
 * één bulk-actie. `voertuigIds` leeg/weggelaten = alle inzetbare voertuigen
 * (niet AFGESCHREVEN/VERKOCHT). `locatieId` is de sleutelkast waar de
 * reservesleutels op voorraad komen.
 */
export const uitrolSleutelsSchema = z.object({
  artikelId: z.string().min(1),
  voertuigIds: z.array(z.string().min(1)).max(250).optional().nullable(),
  locatieId: z.string().min(1),
  ookReserve: z.boolean().default(true),
})
export type UitrolSleutelsInput = z.infer<typeof uitrolSleutelsSchema>

/** Eén regel resultaat per voertuig na een sleutel-uitrol-run. */
export interface UitrolSleutelRegelDTO {
  voertuigId: string
  kenteken: string
  hoofdsleutel: 'AANGEMAAKT' | 'OVERGESLAGEN' | 'FOUT'
  hoofdsleutelNummer: string | null
  hoofdsleutelUitgegevenAan: string | null
  reserve: 'AANGEMAAKT' | 'OVERGESLAGEN' | 'FOUT' | 'NIET_GEVRAAGD'
  reserveNummer: string | null
  melding: string | null
}
